import { BaseResponse, QuerySchema } from "../base";
import {
  MediaListParamsBody,
  MediaModel,
  MediaParamsBody,
  MediaFormData,
  ChapterInterface,
  AddChapterParams,
  MediaSecurityFormData,
  HostSubSection,
  AssetItem,
  MediaMeta,
  CreditInterface,
  LiveMeta,
  SubtitleModel,
  HomeListParams,
  HomeListApiResponse,
  TrimMediaParams,
  TrimMediaResponse,
  SectionEntity,
  articleInfoParams,
  DenmarkTaxonomyCodeFetchItem,
  DenmarkTaxonomyCodeFetchParams,
  Keyword,
  ArticleSearchParams,
  ArticleSearchItem,
  LivePreviewItem,
  LiveUrlItem,
} from "./model";
import { sendReqToController, RpcHttpClient } from "../network";
import { AdminPreset, AllAdminPresets, PresetListModel } from "../user/model";
import { isSocketCall } from "@/utils";
import {
  FetchPriorityListParams,
  GetRecommendedArticlesParams,
  UpdatePriorityListReq,
  CategoryTagsResult,
} from "./model";

interface RespMediaList extends BaseResponse {
  data: Array<MediaModel>;
  count: number;
}

interface RespLiveList extends BaseResponse {
  data: Array<LiveMeta>;
}

export const ListMediasApi = async (
  params: MediaListParamsBody
): Promise<RespMediaList> => {
  const query: QuerySchema = {
    size: params.rows || 10,
    from: (!params.page ? 0 : params.page - 1) * (params.rows || 10),
    foreign_keys: params.foreign_keys,
    ...(params.title && { min_should: 2 }),
  };
  delete params.rows;
  delete params.page;
  delete params.foreign_keys;

  if (params.state) {
    query.must = { state: String(params.state) };
  }

  if (params.range) {
    query.range = { ...params.range };
  }
  if (params.rangeMD) {
    query.range = { ...query.range, ...params.rangeMD };
  }
  if (params.must_not) {
    query.must_not = {
      ...params.must_not,
      status: -99,
      media_status: 3,
    };
  } else {
    query.must_not = { status: -99 };
  }

  if (params.orientation) {
    query.must = { orientation: params.orientation };
  }

  if (params.media_ref) {
    query.must = { "media_ref.rel": params.media_ref };
  }

  // Handle `agencies`
  if (params.agencies) {
    query.must = { "agencies.category": Number(params.agencies) };
  }
  if (params.media_duration) {
    query.range = {
      ...query.range, // Preserve existing range filters
      "meta.duration": params.media_duration["meta.duration"],
    };
  }
  if (params.content_used) {
    query.must = { "child_media.team": params.content_used };
  }

  // Handle `must`
  if (params.type) {
    query.must = { "meta.type": params.type["meta.type"] };
  }
  if (params.sort) {
    query.sort = { ...params.sort };
  } else {
    query.sort = {
      ctuser: "desc",
    };
  }

  if (params["publishing_destinations"]) {
    query.must = { "socialembeds.job_type": params["publishing_destinations"] };
  }
  const ctbyFilter = (params as any).ctby;
  if (ctbyFilter) {
    query.must = { ...query.must, ctby: ctbyFilter };
  }

  if (params?.include_archived) {
    query.include_archived = params.include_archived;
  }

  if (params?.archive) {
    query.must = {
      ...query.must,
      archive: params.archive,
    };
  }

  if (String(params?.media_status) === "3") {
    delete params?.media_status;
  }

  // Extract remaining parameters that are neither `range`, `media_duration`, nor `type`
  const remainingParams = { ...params };
  delete remainingParams.range;
  delete remainingParams.media_duration;
  delete remainingParams.type;
  delete remainingParams.must_not;
  delete remainingParams.publishing_destinations;
  delete remainingParams.agencies;
  delete remainingParams.media_ref;
  delete remainingParams.content_used;
  delete remainingParams.state;
  delete remainingParams.sort;
  delete remainingParams.rangeMD;
  delete (remainingParams as any).ctby;
  delete remainingParams.include_archived;
  delete remainingParams.archive;
  delete remainingParams.orientation;

  if (Object.keys(remainingParams).length > 0) {
    query.must = { ...query.must, ...remainingParams }; // Merge additional filters into must
  }
  if (params.title) {
    delete query?.must?.["title"];
    let sTitle = "";
    for (let i = 0; i < params.title.length; i++) {
      sTitle = sTitle + params.title[i];
    }
    query.search = {
      query: sTitle,
      fields: [
        {
          field: "stitle",
          boost: 7,
        },
        {
          field: "tags",
          boost: 7,
        },
        {
          field: "desc",
          boost: 7,
        },
      ],
    };
  }

  const response = await sendReqToController("media.list", query, isSocketCall);
  try {
    const list: Array<MediaModel> = [];
    if (Array.isArray(response.result.list)) {
      response.result.list.map((media: any) => {
        if (!media.socials || media.socials.length === 0) media.socials = [];
        list.push(media);
      });
    } else if (response.error != "") {
      response.error = "invalid media list";
    }
    return {
      count: response.result.count,
      data: list,
      error: response.error,
      status: response.code,
    };
  } catch (error) {
    return {
      count: 0,
      data: [],
      error: "failed to validate media list response " + error,
      status: response.code,
    };
  }
};

export const GlobalSearchListMediasApi = async (
  params: MediaListParamsBody
): Promise<RespMediaList> => {
  const response = await sendReqToController(
    "media.search",
    params,
    isSocketCall
  );
  try {
    const list: Array<MediaModel> = [];
    if (Array.isArray(response.result.list)) {
      response.result.list.map((media: any) => {
        if (!media.socials || media.socials.length === 0) media.socials = [];
        list.push(media);
      });
    } else if (response.error != "") {
      response.error = "invalid media list";
    }
    return {
      count: response.result.count,
      data: list,
      error: response.error,
      status: response.code,
    };
  } catch (error) {
    return {
      count: 0,
      data: [],
      error: "failed to validate media list response " + error,
      status: response.code,
    };
  }
};

export const DiscoverListMediasApi = async (
  params: MediaListParamsBody
): Promise<RespMediaList> => {
  const query: QuerySchema = {
    size: params.rows || 10,
    from: (!params.page ? 0 : params.page - 1) * (params.rows || 10),
    foreign_keys: [...(params.foreign_keys || []), "teamdata", "agencydata"],
    ...(params.title && { min_should: 2 }),
  };
  delete params.rows;
  delete params.page;
  delete params.foreign_keys;

  if (params.state) {
    query.must = { state: String(params.state) };
  }

  if (params.range) {
    query.range = { ...params.range };
  }
  if (params.rangeMD) {
    query.range = { ...query.range, ...params.rangeMD };
  }
  if (params.must_not) {
    query.must_not = {
      ...params.must_not,
      status: -99,
      sourceExits: 0,
    };
  } else {
    query.must_not = { status: -99, sourceExits: 0, sourceExists: 0 };
  }

  // Handle `agencies`
  if (params.agencies) {
    query.must = { "agencies.category": Number(params.agencies) };
  }
  if (params.media_duration) {
    query.range = {
      ...query.range, // Preserve existing range filters
      "meta.duration": params.media_duration["meta.duration"],
    };
  }
  if (params.content_used) {
    query.must = { "child_media.team": params.content_used };
  }

  // Handle `must`
  if (params.type) {
    query.must = { "meta.type": params.type["meta.type"] };
  }
  if (params.sort) {
    query.sort = params.sort;
  } else {
    query.sort = { mtuser: "desc" };
  }

  if (params["publishing_destinations"]) {
    query.must = { "socialembeds.job_type": params["publishing_destinations"] };
  }

  // Extract remaining parameters that are neither `range`, `media_duration`, nor `type`
  const remainingParams = { ...params };
  delete remainingParams.range;
  delete remainingParams.media_duration;
  delete remainingParams.type;
  delete remainingParams.must_not;
  delete remainingParams.publishing_destinations;
  delete remainingParams.agencies;
  delete remainingParams.content_used;
  delete remainingParams.state;
  delete remainingParams.sort;

  if (Object.keys(remainingParams).length > 0) {
    query.must = { ...query.must, ...remainingParams }; // Merge additional filters into must
  }
  if (params.title) {
    delete query?.must?.["title"];
    let sTitle = "";
    for (let i = 0; i < params.title.length; i++) {
      sTitle = sTitle + params.title[i];
    }
    query.search = {
      query: sTitle,
      fields: [
        {
          field: "stitle",
          boost: 7,
        },
        {
          field: "tags",
          boost: 7,
        },
        {
          field: "desc",
          boost: 7,
        },
      ],
    };
  }

  const response = await sendReqToController(
    "media.discover_list",
    query,
    isSocketCall
  );
  try {
    const list: Array<MediaModel> = [];
    if (Array.isArray(response.result.list)) {
      response.result.list.map((media: any) => {
        if (!media.socials || media.socials.length === 0) media.socials = [];
        list.push(media);
      });
    } else if (response.error != "") {
      response.error = "invalid media list";
    }
    return {
      count: response.result.count,
      data: list,
      error: response.error,
      status: response.code,
    };
  } catch (error) {
    return {
      count: 0,
      data: [],
      error: "failed to validate media list response " + error,
      status: response.code,
    };
  }
};

export const WiredDiscoverListMediasApi = async (
  params: MediaListParamsBody
): Promise<RespMediaList> => {
  const query: QuerySchema = {
    size: params.rows || 10,
    from: (!params.page ? 0 : params.page - 1) * (params.rows || 10),
    foreign_keys: [...(params.foreign_keys || []), "teamdata", "agencydata"],
    ...(params.title && { min_should: 2 }),
  };
  delete params.rows;
  delete params.page;
  delete params.foreign_keys;

  if (params.state) {
    query.must = { state: String(params.state) };
  }

  if (params.range) {
    query.range = { ...params.range };
  }
  if (params.rangeMD) {
    query.range = { ...query.range, ...params.rangeMD };
  }
  if (params.must_not) {
    query.must_not = {
      ...params.must_not,
      status: -99,
      sourceExits: 0,
    };
  } else {
    query.must_not = { status: -99, sourceExits: 0, sourceExists: 0 };
  }

  // Handle `agencies`
  if (params.agencies) {
    query.must = { "agencies.category": Number(params.agencies) };
  }
  if (params.media_duration) {
    query.range = {
      ...query.range, // Preserve existing range filters
      "meta.duration": params.media_duration["meta.duration"],
    };
  }
  if (params.content_used) {
    query.must = { "child_media.team": params.content_used };
  }

  // Handle `must`
  if (params.type) {
    query.must = { "meta.type": params.type["meta.type"] };
  }
  if (params.sort) {
    query.sort = params.sort;
  } else {
    query.sort = { mtuser: "desc" };
  }

  if (params["publishing_destinations"]) {
    query.must = { "socialembeds.job_type": params["publishing_destinations"] };
  }

  // Extract remaining parameters that are neither `range`, `media_duration`, nor `type`
  const remainingParams = { ...params };
  delete remainingParams.range;
  delete remainingParams.media_duration;
  delete remainingParams.type;
  delete remainingParams.must_not;
  delete remainingParams.publishing_destinations;
  delete remainingParams.agencies;
  delete remainingParams.content_used;
  delete remainingParams.state;
  delete remainingParams.sort;

  if (Object.keys(remainingParams).length > 0) {
    query.must = { ...query.must, ...remainingParams }; // Merge additional filters into must
  }
  if (params.title) {
    delete query?.must?.["title"];
    let sTitle = "";
    for (let i = 0; i < params.title.length; i++) {
      sTitle = sTitle + params.title[i];
    }
    query.search = {
      query: sTitle,
      fields: [
        {
          field: "stitle",
          boost: 7,
        },
        {
          field: "tags",
          boost: 7,
        },
        {
          field: "desc",
          boost: 7,
        },
      ],
    };
  }

  const response = await sendReqToController(
    "media.discover_list_wired",
    query,
    isSocketCall
  );
  try {
    const list: Array<MediaModel> = [];
    if (Array.isArray(response.result.list)) {
      response.result.list.map((media: any) => {
        if (!media.socials || media.socials.length === 0) media.socials = [];
        list.push(media);
      });
    } else if (response.error != "") {
      response.error = "invalid media list";
    }
    return {
      count: response.result.count,
      data: list,
      error: response.error,
      status: response.code,
    };
  } catch (error) {
    return {
      count: 0,
      data: [],
      error: "failed to validate media list response " + error,
      status: response.code,
    };
  }
};

export const LiveListApi = async (): Promise<RespLiveList> => {
  const query: QuerySchema = {
    size: 5000,
    min_should: 1,
  };
  const response = await sendReqToController(
    "live.listchannels",
    query,
    isSocketCall
  );

  try {
    return {
      // count: response.result.count,
      data: response.result.list,
      error: response.error,
      status: response.code,
    };
  } catch (error) {
    return {
      data: [],
      error: "failed to validate media list response " + error,
      status: response.code,
    };
  }
};

export const GetRecommendedArticlesApi = async (
  params: GetRecommendedArticlesParams
): Promise<any> => {
  const response = await sendReqToController(
    "denmark.recommendations",
    params,
    isSocketCall
  );
  try {
    return {
      data: response.result,
      error: response.error,
      status: response.code,
    };
  } catch (error) {
    return {
      data: [],
      error: "failed to validate media list response " + error,
      status: response.code,
    };
  }
};

export const GetTokenForDownloadApi = async (params: {
  id: string;
}): Promise<any> => {
  const response = await sendReqToController(
    "download.downloadmedia",
    params,
    isSocketCall
  );
  return {
    data: response.result,
    error: response.error,
    status: response.code,
  };
};

export const SearchListMediasApi = async (
  params: MediaListParamsBody
): Promise<RespMediaList> => {
  const query: QuerySchema = {
    size: params.rows || 50,
    from: (!params.page ? 0 : params.page - 1) * (params.rows || 50),
    foreign_keys: params.foreign_keys,
    ...(params.stitle?.length !== 10 && { min_should: 1 }),
  };
  delete params.rows;
  delete params.page;
  delete params.foreign_keys;

  if (params.range) {
    query.range = { ...params.range };
  }
  if (params.media_duration) {
    query.range = {
      ...query.range, // Preserve existing range filters
      "meta.duration": params.media_duration["meta.duration"],
    };
  }

  // Handle `must`
  // if (params.type) {
  //   query.must = { 'meta.type': params.type['meta.type'] };
  // }
  if (
    params?.stitle?.length === 10 &&
    (params.stitle.startsWith("1x") || params.stitle.startsWith("17"))
  ) {
    query.must = { id: [params.stitle] };
  } else {
    query.search = {
      query: params.stitle,
      fields: [
        {
          field: "stitle",
          boost: 7,
        },
        {
          field: "tags",
          boost: 7,
        },
        {
          field: "desc",
          boost: 7,
        },
      ],
    };
  }
  // query.should = {
  //   "stitle.sat": {
  //     qtype: "match",
  //     value: params.stitle,
  //   },
  //   id: {
  //     qtype: "match",
  //     value: params.stitle,
  //   },
  // };

  query.sort = { mtuser: "desc" };

  if (params.must_not) {
    query.must_not = {
      ...query.must_not,
      ...params.must_not,
      media_status: 3,
    };
  }

  // Extract remaining parameters that are neither `range`, `media_duration`, nor `type`
  const remainingParams = { ...params };
  delete remainingParams.range;
  delete remainingParams.media_duration;
  delete remainingParams.type;
  delete remainingParams.stitle;
  delete remainingParams.key;
  delete remainingParams.must_not;
  query.must = {
    ...query.must,
    status: "5",
    ...remainingParams,
  };

  // if (Object.keys(remainingParams).length > 0) {
  //   query.must = { ...query.must, ...remainingParams }; // Merge additional filters into must
  // }

  const response = await sendReqToController("media.list", query, isSocketCall);
  try {
    const list: Array<MediaModel> = [];
    if (Array.isArray(response.result.list)) {
      response.result.list.map((media: any) => {
        if (!media.socials || media.socials.length === 0) media.socials = [];
        list.push(media);
      });
    } else if (response.error != "") {
      response.error = "invalid media list";
    }
    return {
      count: response.result.count,
      data: list,
      error: response.error,
      status: response.code,
    };
  } catch (error) {
    return {
      count: 0,
      data: [],
      error: "failed to validate media list response " + error,
      status: response.code,
    };
  }
};

interface RespMedia extends BaseResponse {
  data: MediaModel;
}

export const DownloadCheckApi = async (params: any): Promise<any> => {
  const response = await sendReqToController(
    "download.check",
    params,
    isSocketCall
  );
  return {
    data: response.result,
    error: response.error,
    status: response.code,
  };
};

export const GetMediaApi = async (
  params: MediaParamsBody
): Promise<RespMedia> => {
  const query = {
    ...params,
    foreign_keys: ["socials", "ctbydata", "mtbydata", "agencydata", "teamdata"],
  };
  const response = await sendReqToController("media.get", query, isSocketCall);
  try {
    const media: MediaModel = response.result;
    if (media) {
      if (!media.socials) {
        media.socials = [];
      } else {
        media.socials = media.socials.map((social: any) => {
          return {
            ...social,
            tags: social?.tags,
          };
        });
      }
      return {
        data: media,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: {} as MediaModel,
        error: "No Media Found",
        status: response.code,
      };
    }
  } catch {
    return {
      data: {} as MediaModel,
      error: "Failed to Validate Media Model",
      status: response.code,
    };
  }
};

interface RespMediaDelete extends BaseResponse {
  message: string;
}

export interface ReuploadMediaParams {
  id: string;
  damid: string;
  team?: string;
  agency?: string;
  asset_type?: string;
  is_short?: number;
  fid?: string;
  title?: string;
}

interface RespReuploadMedia extends BaseResponse {
  data: {
    id: string;
    jwt: string;
  } | null;
}

export const ReuploadMediaApi = async (
  params: ReuploadMediaParams
): Promise<RespReuploadMedia> => {
  const response = await sendReqToController(
    "media.reupload",
    params,
    isSocketCall
  );
  return {
    data: response.result,
    error: response.error,
    status: response.code,
  };
};

// Uploads a thumbnail onto a media from a source image URL - used by
// "Reuse Meta" -> select media to carry the source media's thumbnail onto
// the media currently being edited, since mapMediaToPresetPayload only
// copies metadata fields, not the binary thumbnail itself.
export interface UrlUploadMediaThumbnailParams {
  fid: string;
  title: string;
  source?: string;
  name: "thumb";
  type: "thumb";
  isShorts: boolean;
  is_short?: number;
  id: string;
  cdn?: { id?: string }[];
  ctype?: string;
  damid: string;
  stitle?: string[];
  desc?: string;
  default: boolean;
  business?: string;
  downloads: {
    image: {
      url: string;
      // Which slot on the target media's `images` this upload actually fills -
      // e.g. "thumb" (landscape), "portrait", "social", "podcast", or any
      // other type present on the source media. `name`/`type` above stay
      // "thumb" for the backend's asset-kind routing; thumb_type carries the
      // specific slot.
      thumb_type?: string;
    };
  };
}

interface RespUrlUploadMediaThumbnail extends BaseResponse {
  data: any;
}

export const UrlUploadMediaThumbnailApi = async (
  params: UrlUploadMediaThumbnailParams
): Promise<RespUrlUploadMediaThumbnail> => {
  const response = await sendReqToController(
    "assets.urlimageupload",
    params,
    isSocketCall
  );
  return {
    data: response.result,
    error: response.error,
    status: response.code,
  };
};

export const DeleteMediaApi = async (
  params: MediaParamsBody
): Promise<RespMediaDelete> => {
  const response = await sendReqToController(
    "media." + params.method,
    params,
    isSocketCall
  );
  if (response.code === 200 && response.result.length !== 0) {
    return {
      message: response.result,
      error: response.error,
      status: response.code,
    };
  } else {
    return {
      message: "Error While Deleting Media",
      error: response.error,
      status: response.code,
    };
  }
};

export const DeleteDenmarkPublishApi = async (
  params: any
): Promise<RespMediaDelete> => {
  const response = await sendReqToController(
    "media.remove",
    params,
    isSocketCall
  );
  if (response.code === 200 && response.result.length !== 0) {
    return {
      message: response.result,
      error: response.error,
      status: response.code,
    };
  } else {
    return {
      message: "Error While Deleting Denmark Publish",
      error: response.error,
      status: response.code,
    };
  }
};

export const UpdateMediaApi = async (
  params: MediaFormData | MediaSecurityFormData
): Promise<RespMedia> => {
  const response = await sendReqToController(
    "media.update",
    params,
    isSocketCall
  );
  try {
    const media: MediaModel = response.result;
    if (media) {
      return {
        data: media,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: {} as MediaModel,
        error: response.error,
        status: response.code,
      };
    }
  } catch {
    return {
      data: {} as MediaModel,
      error: "Failed to Validate Media Model",
      status: response.code,
    };
  }
};

export interface ChunkInfoItem {
  number: number;
  size: number;
}

interface RespChunkInfo extends BaseResponse {
  data: ChunkInfoItem[];
}

// Hits asset-dev.sli.ke/rpc (NX_APP_DOWNLOAD_URL), not the app-backend RPC
// endpoint sendReqToController targets, and authenticates with the per-upload
// token rather than the session/socket auth — same as the resumable upload
// requests for this upload_id.
export const GetChunkInfoApi = async (params: {
  upload_id: string;
  token: string;
}): Promise<RespChunkInfo> => {
  const response = await RpcHttpClient({
    host: process.env["NX_APP_DOWNLOAD_URL"],
    headers: { token: params.token },
    body: {
      method: "asset.chunkinfo",
      params: { upload_id: params.upload_id },
    },
    // The asset host authenticates via the per-upload `token` header, not
    // cookies — every other call to this host (the actual chunk-upload
    // XHR/fetch in both upload engines) sends credentials: "omit"/
    // withCredentials: false for the same reason. RpcHttpClient defaults to
    // "include", which turns this into a credentialed cross-site request the
    // asset-dev CORS policy doesn't allow, so the browser blocks the response
    // even though curl/Postman (no CORS enforcement) see it succeed.
    credentials: "omit",
    parser: async (res) => {
      const result = await res.json();
      return {
        code: res.ok ? 200 : res.status,
        error: result.error,
        result: result.result,
      };
    },
  });
  try {
    const list: ChunkInfoItem[] = response.result ?? [];
    return {
      data: list,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: [] as ChunkInfoItem[],
      error: "Failed to Fetch Chunk Info",
      status: response.code,
    };
  }
};

interface RespAssets extends BaseResponse {
  data: AssetItem[];
}

export const AssetsAPI = async (pid: string): Promise<RespAssets> => {
  const response = await sendReqToController(
    "assets.list",
    { pid: pid },
    isSocketCall
  );
  try {
    const assets: Array<AssetItem> = response.result;
    return {
      data: assets,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: [] as Array<AssetItem>,
      error: "Failed to Fetch Assets",
      status: response.code,
    };
  }
};

interface Host {
  id: string;
  name: any;
}

interface RespHosts extends BaseResponse {
  data: Array<Host>;
}

export const fetchHostsApi = async (params: any): Promise<RespHosts> => {
  const response = await sendReqToController(
    "denmark.hosts",
    params,
    isSocketCall
  );
  try {
    const hosts: Array<Host> = Object.entries(response.result).map(
      ([key, value]) => ({
        id: key,
        name: value,
      })
    );

    if (hosts) {
      return {
        data: hosts,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: [] as Array<Host>,
        error: response.error,
        status: response.code,
      };
    }
  } catch {
    return {
      data: [] as Array<Host>,
      error: "Failed to Fetch Hosts",
      status: response.code,
    };
  }
};

export const generateClipApi = async (params: any): Promise<any> => {
  const response = await sendReqToController(
    "chapter.clip_generate",
    params,
    isSocketCall
  );
  return {
    data: response.result,
    error: response.error,
    status: response.code,
  };
};
interface RespHostSubSection extends BaseResponse {
  data: Array<HostSubSection>;
}

export const fetchHostSubSectionAPI = async (
  params: any
): Promise<RespHostSubSection> => {
  const response = await sendReqToController(
    "denmark.search",
    params,
    isSocketCall
  );
  try {
    const section: Array<HostSubSection> = response.result.data;
    if (section) {
      return {
        data: section,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: [] as Array<HostSubSection>,
        error: response.error,
        status: response.code,
      };
    }
  } catch {
    return {
      data: [] as Array<HostSubSection>,
      error: "Failed to Fetch Host SubSection",
      status: response.code,
    };
  }
};

export const fetchDenmarkTaxonomyListApi = async (
  params: any
): Promise<any> => {
  const response = await sendReqToController(
    "denmark.taxonomysuggest",
    params,
    isSocketCall
  );

  try {
    const taxonomyList = response.result;
    if (taxonomyList) {
      return {
        data: taxonomyList,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: [] as Array<any>,
        error: response.error,
        status: response.code,
      };
    }
  } catch {
    return {
      data: [] as Array<any>,
      error: "Failed to Fetch Taxonomy List",
      status: response.code,
    };
  }
};

interface RespDenmarkTaxonomyCodeFetch extends BaseResponse {
  data: DenmarkTaxonomyCodeFetchItem[];
}

export const fetchDenmarkTaxonomyByCodeApi = async (
  params: DenmarkTaxonomyCodeFetchParams
): Promise<RespDenmarkTaxonomyCodeFetch> => {
  const response = await sendReqToController(
    "denmark.denmarkcodefetch",
    params,
    isSocketCall
  );
  try {
    const raw = response.result;
    const list: DenmarkTaxonomyCodeFetchItem[] = Array.isArray(raw)
      ? raw
      : raw && Array.isArray(raw.list)
        ? raw.list
        : [];
    return {
      data: list,
      error: response.error ?? "",
      status: response.code,
    };
  } catch {
    return {
      data: [],
      error: "Failed to fetch taxonomy by code",
      status: 0,
    };
  }
};

export interface SectionListRequest {
  parentId: number;
  parentsParentId: number;
  hostId: number;
  startIndex: number;
  recordsPerPage: number;
  listActivationStatus: number[];
}

export const fetchDenmarkSectionListApi = async (params: any): Promise<any> => {
  const response = await sendReqToController(
    "denmark.sections",
    params,
    isSocketCall
  );

  try {
    const sectionList = response.result.listCmsEntities;
    if (sectionList) {
      return {
        data: sectionList,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: [] as Array<any>,
        error: response.error,
        status: response.code,
      };
    }
  } catch {
    return {
      data: [] as Array<any>,
      error: "Failed to Fetch Section List",
      status: response.code,
    };
  }
};

export const fetchArticlesApi = async (
  params: any
): Promise<RespHostSubSection> => {
  const response = await sendReqToController(
    "denmark.articlesearch",
    params,
    isSocketCall
  );
  try {
    const articles = response.result;
    if (articles && Array.isArray(articles) && articles.length > 0) {
      return {
        data: articles,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: [] as Array<any>,
        error: response.error,
        status: response.code,
      };
    }
  } catch {
    return {
      data: [] as Array<any>,
      error: "Failed to Fetch Articles",
      status: response.code,
    };
  }
};

export const trimMediaApi = async (
  params: TrimMediaParams
): Promise<TrimMediaResponse> => {
  const response = await sendReqToController(
    "media.trimming",
    params,
    isSocketCall
  );
  try {
    if (response.code === 200) {
      return {
        data: response.result,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: {},
        error: response.error,
        status: response.code,
      };
    }
  } catch {
    return {
      data: {},
      error: "Failed to trim media",
      status: response.code,
    };
  }
};

export const callPublishMediaApi = async (params: any) => {
  const response = await sendReqToController(
    "socialpublish.create",
    params,
    isSocketCall
  );
  try {
    const result: any = response.result;
    if (result) {
      return {
        data: result,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: {} as MediaModel,
        error: response.error,
        status: response.code,
      };
    }
  } catch {
    return {
      data: {} as MediaModel,
      error: "Failed to publish media",
      status: response.code,
    };
  }
};

export const getCategoryListApi = async (): Promise<any> => {
  const response = await sendReqToController("meta.list", {}, isSocketCall);
  return {
    data: response.result,
    error: response.error,
    status: response.code,
  };
};

export const createCategoryApi = async (params: any): Promise<any> => {
  const response = await sendReqToController(
    `meta.create`,
    params,
    isSocketCall
  );
  try {
    const result: any = response.result;
    if (result) {
      return {
        data: result,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: {} as MediaModel,
        error: response.error,
        status: response.code,
      };
    }
  } catch {
    return {
      data: {} as MediaModel,
      error: "Failed to create category",
      status: response.code,
    };
  }
};

export const updateCategoryApi = async (params: any): Promise<any> => {
  const response = await sendReqToController(
    "meta.update",
    params,
    isSocketCall
  );
  try {
    const result: any = response.result;
    if (result) {
      return {
        data: result,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: {} as MediaModel,
        error: response.error,
        status: response.code,
      };
    }
  } catch {
    return {
      data: {} as MediaModel,
      error: response.error,
      status: response.code,
    };
  }
};

export const deleteCategoryApi = async (params: any): Promise<any> => {
  const payload = { ...params.item, type: params.type };

  const response = await sendReqToController(
    `meta.delete`,
    payload,
    isSocketCall
  );
  return {
    data: response.result,
    error: response.error,
    status: response.code,
  };
};
interface RespMediaMeta extends BaseResponse {
  data: MediaMeta;
}

export const updatePublishMediaApi = async (
  params: any
): Promise<RespMediaMeta> => {
  const response = await sendReqToController(
    "socialpublish.update",
    params,
    isSocketCall
  );
  try {
    const result: MediaMeta = response.result;
    if (result) {
      return {
        data: result,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: {} as MediaMeta,
        error: response.error,
        status: response.code,
      };
    }
  } catch {
    return {
      data: {} as MediaMeta,
      error: "Failed to Load Media Meta",
      status: response.code,
    };
  }
};

interface UpdateVideoResult {
  message: string;
  video_id: string;
}

interface RespUpdateVideo extends BaseResponse {
  data: UpdateVideoResult;
}

export const updateVideoPublishMediaApi = async (
  params: any
): Promise<RespUpdateVideo> => {
  const response = await sendReqToController(
    "socialpublish.update_video",
    params,
    isSocketCall
  );
  try {
    const result: UpdateVideoResult = response.result;
    if (result) {
      return {
        data: result,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: {} as UpdateVideoResult,
        error: response.error,
        status: response.code,
      };
    }
  } catch {
    return {
      data: {} as UpdateVideoResult,
      error: "Failed to Update Video",
      status: response.code,
    };
  }
};

interface VodDetails {
  category: string;
  description?: string;
  playlist?: string;
  privacy: string;
  recorded_date: string;
  tags?: string[];
  thumb: string;
  title: string;
}

interface GetVodDetailsResult {
  message: string;
  video_id: string;
  vod_details: VodDetails;
}

interface RespGetVodDetails extends BaseResponse {
  data: GetVodDetailsResult;
}

export const getVodDetailsApi = async (params: {
  video_id: string;
}): Promise<RespGetVodDetails> => {
  const response = await sendReqToController(
    "socialpublish.get",
    params,
    isSocketCall
  );
  try {
    const result: GetVodDetailsResult = response.result;
    if (result) {
      return {
        data: result,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: {} as GetVodDetailsResult,
        error: response.error,
        status: response.code,
      };
    }
  } catch {
    return {
      data: {} as GetVodDetailsResult,
      error: "Failed to Get VOD Details",
      status: response.code,
    };
  }
};

export interface MediaMetaParams {
  platform: string;
  id: string;
  channel?: string;
}

export const MediaMetaApi = async (params: MediaMetaParams) => {
  const response = await sendReqToController(
    "media.getembedMeta",
    params,
    isSocketCall
  );
  try {
    const result: any = response.result;
    if (result) {
      return {
        data: result,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: {} as MediaModel,
        error: response.error,
        status: response.code,
      };
    }
  } catch {
    return {
      data: {} as MediaModel,
      error: "Failed to update media",
      status: response.code,
    };
  }
};

export interface CustomVideoParams {
  url: string;
  type: string;
  content: string;
}

export const ValidateCustomSourceApi = async (params: CustomVideoParams) => {
  const response = await sendReqToController(
    "assets.validate",
    params,
    isSocketCall
  );
  return {
    data: response.result,
    error: response.error,
    status: response.code,
    result: response.result,
  };
};

interface RespAddChapter extends BaseResponse {
  data: ChapterInterface;
}

const parseChapterTags = (tags: unknown): string[] => {
  if (Array.isArray(tags)) return tags.filter(Boolean).map(String);
  if (typeof tags === "string") {
    return tags
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
  }
  return [];
};

export const GetAddChapterApi = async (
  params: AddChapterParams
): Promise<RespAddChapter> => {
  try {
    const response = await sendReqToController(
      "chapter.create",
      params,
      isSocketCall
    );
    const chapter: ChapterInterface = response.result
      ? { ...response.result, tags: parseChapterTags(response.result.tags) }
      : ({} as ChapterInterface);

    return {
      data: chapter,
      error:
        response.error || (chapter ? null : "Failed to Validate Chapter Model"),
      status: response.code,
    };
  } catch {
    return {
      data: {} as ChapterInterface,
      error: "Failed to Add Chapter",
      status: 500,
    };
  }
};

interface RespDeleteChapter extends BaseResponse {
  message?: string;
}

export const GetDeleteChapterApi = async (
  id: string
): Promise<RespDeleteChapter> => {
  try {
    const response = await sendReqToController(
      "chapter.delete",
      { id },
      isSocketCall
    );

    return {
      message: response?.result || "",
      error: response.error || null,
      status: response.code,
    };
  } catch {
    return {
      message: "",
      error: "Failed to Delete Chapter",
      status: 500, // Default to 500 for server errors
    };
  }
};

interface RespListChapter extends BaseResponse {
  mediaId: string;
  count: number;
  data: Array<ChapterInterface>;
}

export const GetCurrentChapterApi = async (
  id: string
): Promise<RespAddChapter> => {
  const response = await sendReqToController(
    "chapter.get",
    { id },
    isSocketCall
  );
  try {
    const chapter: ChapterInterface = {
      ...response.result,
      tags: parseChapterTags(response.result?.tags),
      clip_start: response.result?.clip_start,
      clip_end: response.result?.clip_end,
    };
    return {
      data: chapter,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: {} as ChapterInterface,
      error: "Failed to fetch current chapter",
      status: 500,
    };
  }
};

export const GetListChaptersApi = async (
  id: string
): Promise<RespListChapter> => {
  try {
    const response = await sendReqToController(
      "chapter.list",
      {
        must: {
          media: id,
        },
        sort: { mt: "asc" },
      },
      isSocketCall
    );

    if (response.code === 200) {
      return {
        mediaId: id,
        count: response.result?.count || 0,
        data:
          response.result?.list.map((item: any) => {
            const chapter: ChapterInterface = {
              title: item.title,
              description: item.description,
              tags: parseChapterTags(item.tags),
              start_time: item.start_time,
              end_time: item.end_time,
              duration: item.duration,
              thumb: item.thumb,
              base64: item.base64 || [],
              media: item.media,
              id: item.id,
              thumbBase64: item.thumbBase64,
              base: item.base,
              ct: item.ct,
              mt: item.mt,
              poster: item.poster,
              enable: item.enable,
              is_change: item.is_change,
              clip_id: item.clip_id,
              clip_start: item.clip_start,
              clip_end: item.clip_end,
            };
            return chapter;
          }) || [],
        error: null,
        status: response.code,
      };
    } else {
      return {
        mediaId: id,
        count: 0,
        data: [],
        error: response.error || "Failed to fetch chapters.",
        status: response.code,
      };
    }
  } catch {
    return {
      mediaId: id,
      count: 0,
      data: [],
      error: "An unexpected error occurred while fetching chapters.",
      status: 500,
    };
  }
};

interface UpdateChapterParams extends AddChapterParams {
  id: string;
}

export const GetUpdateChapterApi = async (
  params: UpdateChapterParams
): Promise<RespAddChapter> => {
  try {
    const response = await sendReqToController(
      "chapter.update",
      params,
      isSocketCall
    );

    if (response.code === 200) {
      return {
        data: response.result || ({} as ChapterInterface),
        error: null,
        status: response.code,
      };
    } else {
      return {
        data: {} as ChapterInterface,
        error: response.error || "Failed to update chapter.",
        status: response.code,
      };
    }
  } catch {
    return {
      data: {} as ChapterInterface,
      error: "An unexpected error occurred while updating the chapter.",
      status: 500, // Fallback status code for errors
    };
  }
};

export const CreditListApi = async (params: string): Promise<any> => {
  const query: QuerySchema = {
    size: 5000,
    min_should: 1,
  };
  query.sort = { mt: "desc" };

  query.must = {
    ...(params
      ? {
          "name.sat": params.trim().toLowerCase().split(/\s+/).filter(Boolean),
        }
      : {}),
  };

  try {
    const response = await sendReqToController(
      "credits.list",
      query,
      isSocketCall
    );
    if (response.code === 200) {
      return {
        data: response.result.list || ({} as CreditInterface[]),
        error: null,
        status: response.code,
      };
    } else {
      return {
        data: {} as CreditInterface,
        error: response.error || "Failed to fetch credit.",
        status: response.code,
      };
    }
  } catch {
    return {
      data: {} as CreditInterface,
      error: "An unexpected error occurred while fetching the credit.",
      status: 500, // Fallback status code for errors
    };
  }
};

interface RespAddPreset extends BaseResponse {
  data: Array<PresetListModel>;
}

export const AddPresetApi = async (
  params: PresetListModel
): Promise<RespAddPreset> => {
  const query: QuerySchema = {
    foreign_keys: ["cybydata", "mtbydata"],
  };
  query.must = { ...params };
  const response = await sendReqToController(
    "preset.create",
    params,
    isSocketCall
  );

  try {
    return {
      data: response.result,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: [] as Array<PresetListModel>,
      error: "Failed to add preset",
      status: response.code,
    };
  }
};

interface RespAdminPresetList extends BaseResponse {
  data: AllAdminPresets;
}

export const GetAdminPresetListApi = async (
  params: any
): Promise<RespAdminPresetList> => {
  params.size = 1000;
  const response = await sendReqToController(
    "all_preset.list",
    params,
    isSocketCall
  );
  try {
    return {
      data: response.result,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: {} as AllAdminPresets,
      error: "Failed to fetch admin preset list",
      status: 500,
    };
  }
};
interface RespPresetList extends BaseResponse {
  data: Array<PresetListModel>;
}

export const GetPresetListApi = async (
  params?: any
): Promise<RespPresetList> => {
  const query: QuerySchema = {
    size: 1000,
    foreign_keys: ["cybydata", "mtbydata"],
  };
  query.must = { ...params };

  const response = await sendReqToController(
    "preset.list",
    query,
    isSocketCall
  );
  try {
    return {
      data: response.result.list,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: [] as Array<PresetListModel>,
      error: "Failed to fetch preset list",
      status: 500,
    };
  }
};

interface RespCurrentPresetDetails extends BaseResponse {
  data: PresetListModel;
}

interface RespAdminCurrentPresetDetails extends BaseResponse {
  data: AdminPreset;
}

export const GetCurrentPresetDetailsApi = async (
  params: any
): Promise<RespCurrentPresetDetails> => {
  const response = await sendReqToController(
    "preset.get",
    params,
    isSocketCall
  );
  try {
    return {
      data: response.result,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: {} as PresetListModel,
      error: "Failed to fetch preset list",
      status: 500,
    };
  }
};

export const GetAdminCurrentPresetDetailsApi = async (
  params: any
): Promise<RespAdminCurrentPresetDetails> => {
  params.size = 1000;
  const response = await sendReqToController(
    "all_preset.get",
    params,
    isSocketCall
  );
  try {
    return {
      data: response.result,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: {} as AdminPreset,
      error: "Failed to fetch admin preset details",
      status: 500,
    };
  }
};

interface RespSubtitleList extends BaseResponse {
  data: {
    list?: Array<SubtitleModel>;
    count?: number;
  };
}

export const GetSubtitleListApi = async (
  params: any
): Promise<RespSubtitleList> => {
  const query: QuerySchema = {
    size: 5000,
    must: {
      media: params.id,
    },
  };
  const response = await sendReqToController(
    "subtitles.list",
    query,
    isSocketCall
  );
  try {
    return {
      data: {
        list: response.result.list,
        count: response.result.count,
      },
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: {
        list: [] as Array<SubtitleModel>,
        count: 0,
      },
      error: "Failed to fetch subtitle list",
      status: 500,
    };
  }
};

export const UpdatePresetApi = async (
  params: PresetListModel
): Promise<RespAddPreset> => {
  const response = await sendReqToController(
    "preset.update",
    params,
    isSocketCall
  );
  try {
    return {
      data: response.result,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: [] as Array<PresetListModel>,
      error: "Failed to update preset",
      status: response.code,
    };
  }
};

export const DeletePresetApi = async (
  params: PresetListModel
): Promise<RespAddPreset> => {
  const response = await sendReqToController(
    "preset.delete",
    params,
    isSocketCall
  );
  try {
    return {
      data: response.result,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: [] as Array<PresetListModel>,
      error: "Failed to delete preset",
      status: response.code,
    };
  }
};

interface RespUpdateSubtitle extends BaseResponse {
  data: SubtitleModel;
}

export const UpdateSubtitleApi = async (
  params: SubtitleModel
): Promise<RespUpdateSubtitle> => {
  const response = await sendReqToController(
    "subtitles.update",
    params,
    isSocketCall
  );

  try {
    return {
      data: response.result,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: {} as SubtitleModel,
      error: "Failed to update subtitle",
      status: 500,
    };
  }
};

export const HomeListApi = async (
  params: HomeListParams
): Promise<HomeListApiResponse> => {
  const response = await sendReqToController(
    "dashboard.list",
    params,
    isSocketCall
  );
  try {
    return {
      data: response.result,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: {},
      error: "Failed to fetch home list",
      status: 500,
    };
  }
};

export const fetchPriorityListApi = async (
  params: FetchPriorityListParams
): Promise<any> => {
  const response = await sendReqToController(
    "denmark.fetchpriority",
    params,
    isSocketCall
  );
  try {
    const priorityList = response.result;
    if (priorityList) {
      return {
        data: priorityList,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: {},
        error: response.error,
        status: response.code,
      };
    }
  } catch {
    return {
      data: {},
      error: "Failed to fetch priority list",
      status: response.code,
    };
  }
};

export const updatePriorityListApi = async (
  params: UpdatePriorityListReq
): Promise<any> => {
  const response = await sendReqToController(
    "denmark.updateprioritylist",
    params,
    isSocketCall
  );
  try {
    const result = response.result;
    if (result) {
      return {
        data: result,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: {},
        error: response.error,
        status: response.code,
      };
    }
  } catch {
    return {
      data: {},
      error: "Failed to update priority list",
      status: response.code,
    };
  }
};

export const GetArticleInfoListApi = async (
  params: articleInfoParams
): Promise<any> => {
  const response = await sendReqToController(
    "denmark.articleinfo",
    params,
    isSocketCall
  );
  try {
    return {
      data: response.result,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: {},
      error: response.error,
      status: response.code,
    };
  }
};

export const GetFavoriteDenmarkSectionListApi = async (
  params: any
): Promise<any> => {
  const response = await sendReqToController(
    "denmark.favorites",
    params,
    isSocketCall
  );
  try {
    return {
      data: response.result,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: [] as Array<SectionEntity>,
      error: response.error,
      status: response.code,
    };
  }
};

export const MarkFavoriteApi = async (params: any): Promise<any> => {
  const response = await sendReqToController(
    "denmark.markfavorite",
    params,
    isSocketCall
  );
  try {
    return {
      data: response.result,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: [] as Array<SectionEntity>,
      error: response.error,
      status: response.code,
    };
  }
};

export const RemoveFavoriteApi = async (params: any): Promise<any> => {
  const response = await sendReqToController(
    "denmark.removefavorite",
    params,
    isSocketCall
  );
  try {
    return {
      data: response.result,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: [] as Array<SectionEntity>,
      error: response.error,
      status: response.code,
    };
  }
};

export const GetAddedInListApi = async (params: any): Promise<any> => {
  const response = await sendReqToController(
    "denmark.addedin",
    params,
    isSocketCall
  );
  try {
    return {
      data: response.result,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: {},
      error: response.error,
      status: response.code,
    };
  }
};

interface KeywordsResponse extends BaseResponse {
  data: Keyword[];
}

export const GetKeywordsApi = async (
  hostid: number,
  text: string
): Promise<KeywordsResponse> => {
  const response = await sendReqToController(
    "denmark.seokeyword",
    { hostid, text },
    isSocketCall
  );
  try {
    return {
      data: response.result?.keywords ?? [],
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: [],
      error: response.error,
      status: response.code,
    };
  }
};

interface CategoryTagsResponse extends BaseResponse {
  data: CategoryTagsResult;
}

export const GetCategoryTagsApi = async (
  host: number,
  query: string
): Promise<CategoryTagsResponse> => {
  const response = await sendReqToController(
    "denmark.categorytags",
    { host, query },
    isSocketCall
  );
  try {
    return {
      data: response.result ?? { input: query, suggestions: [] },
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: { input: query, suggestions: [] },
      error: response.error,
      status: response.code,
    };
  }
};

interface ArticleSearchApiResponse extends BaseResponse {
  data: ArticleSearchItem[];
}

export const articleSearchApi = async (
  params: ArticleSearchParams
): Promise<ArticleSearchApiResponse> => {
  const response = await sendReqToController(
    "denmark.articlesearch",
    params,
    isSocketCall
  );
  try {
    const articles = response.result;
    if (articles && Array.isArray(articles) && articles.length > 0) {
      return {
        data: articles,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: [],
        error: response.error,
        status: response.code,
      };
    }
  } catch {
    return {
      data: [],
      error: "Failed to fetch article search results",
      status: response.code,
    };
  }
};

interface LivePreviewApiResponse extends BaseResponse {
  data: LivePreviewItem | null;
}

export const fetchLivePreviewApi = async (params: {
  msid: string;
  hostid: string;
}): Promise<LivePreviewApiResponse> => {
  const response = await sendReqToController(
    "denmark.livepreview",
    { msid: params.msid, hostid: params.hostid },
    isSocketCall
  );
  try {
    const result: LivePreviewItem = response.result;
    return {
      data: result ?? null,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: null,
      error: "Failed to fetch live preview",
      status: response.code,
    };
  }
};

interface DenmarkEntityGetResponse extends BaseResponse {
  data: SectionEntity[];
}

export const fetchDenmarkEntityGetApi = async (params: {
  hostId: number | string;
  msId: number | string;
}): Promise<DenmarkEntityGetResponse> => {
  const response = await sendReqToController(
    "denmark.entityget",
    { hostId: String(params.hostId), msId: String(params.msId) },
    isSocketCall
  );
  try {
    const list = response.result?.listCmsEntities ?? response.result;
    if (list && Array.isArray(list)) {
      return {
        data: list,
        error: response.error,
        status: response.code,
      };
    } else {
      return {
        data: [] as SectionEntity[],
        error: response.error,
        status: response.code,
      };
    }
  } catch {
    return {
      data: [] as SectionEntity[],
      error: "Failed to fetch entity list",
      status: response.code,
    };
  }
};

interface LiveUrlApiResponse extends BaseResponse {
  data: LiveUrlItem | null;
}

export const fetchLiveUrlApi = async (params: {
  msid: string;
  hostid: string;
}): Promise<LiveUrlApiResponse> => {
  const response = await sendReqToController(
    "denmark.liveurl",
    { msid: params.msid, hostid: params.hostid },
    isSocketCall
  );
  try {
    const result: LiveUrlItem = response.result;
    return {
      data: result ?? null,
      error: response.error,
      status: response.code,
    };
  } catch {
    return {
      data: null,
      error: "Failed to fetch live url",
      status: response.code,
    };
  }
};

/** A video row inside a custom playlist's `custom_items`. */
export interface PlaylistCustomItem {
  id: string;
  title?: string;
  artist?: string;
  duration?: string;
  position?: number;
  thumbnail?: string;
}

/** A YouTube channel/playlist a custom playlist is mapped to (`youtube_playlist`). */
export interface PlaylistYoutubeMapping {
  channel_id?: string;
  channel_name?: string;
  playlist_id?: string;
  playlist_name?: string;
  channel_category?: string;
}

/** A Denmark section a custom playlist is mapped to (`denmark_playlist`). */
export interface PlaylistDenmarkMapping {
  channel_name?: string;
  host?: number;
  section?: number;
  folder_path?: string;
  article_category?: string;
  article_type?: string;
}

/** A raw `playlist.list` row (only the fields the UI reads are typed). */
export interface PlaylistListItem {
  id: string;
  title?: string;
  ctby?: string;
  mtby?: string;
  mt?: number;
  ctbydata?: { name?: string };
  mtbydata?: { name?: string };
  custom_items?: PlaylistCustomItem[];
  youtube_playlist?: PlaylistYoutubeMapping[];
  denmark_playlist?: PlaylistDenmarkMapping[];
}

interface RespPlaylistList extends BaseResponse {
  data: Array<PlaylistListItem>;
  count: number;
}

/** `playlist.list` — active/draft custom playlists for a team. */
export const PlaylistListApi = async (params: {
  team: string;
}): Promise<RespPlaylistList> => {
  const response = await sendReqToController(
    "playlist.list",
    {
      from: 0,
      size: 50,
      page: 1,
      pageSize: 50,
      must: {
        playlist_type: "custom",
        status: "active",
        team: params.team,
      },
    },
    isSocketCall
  );
  const list: PlaylistListItem[] = Array.isArray(response.result?.list)
    ? response.result.list
    : [];
  return {
    count: response.result?.count ?? list.length,
    data: list,
    error: response.error,
    status: response.code,
  };
};

interface RespPlaylistGet extends BaseResponse {
  data: PlaylistListItem | null;
}
export const PlaylistGetApi = async (params: {
  id: string;
}): Promise<RespPlaylistGet> => {
  const response = await sendReqToController(
    "playlist.get",
    { id: params.id, foreign_keys: ["ctbydata", "mtbydata"] },
    isSocketCall
  );
  const row: PlaylistListItem | null = response.result?.id
    ? response.result
    : (response.result?.data ?? response.result?.list?.[0] ?? null);
  return {
    data: row,
    error: response.error,
    status: response.code,
  };
};
